import React, { memo, useCallback, useEffect, useRef } from 'react';
import { ModalBackdrop, ModalContainer, ModalContent } from 'is24-corecss';
import { useQueryClient } from '@tanstack/react-query';
import { css } from '@emotion/css';
import ImmoAssistantLoading from './ImmoAssistantLoading';
import BetaBadgeSvg from './BetaBadgeSvg';
import { determineImmoPageTitle, ImmoTrackingContext } from './tracking.util';
import { PROPERTY_HUB_PAGE_TITLE_PREFIX, report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';

const stylesModal = {
  container: css`
    max-width: 1000px;
    width: 100% !important;
    height: 90vh;
    overflow: hidden;
    z-index: 2000;
    @media (max-width: 668px) {
      position: fixed !important;
      display: flex;
      flex-flow: column;
      top: 0;
      width: 100vw !important;
      height: 100vh;
      max-width: 100vw;
      border-radius: 0;
    }
  `,
  modalContentWrapper: css`
    max-height: inherit !important;
    overflow: hidden !important;
    display: flex;
    flex-flow: column;
    height: 100%;
    @media (min-width: 668px) {
      max-height: calc(100vh - 200px) !important;
    }
  `,
  modalContent: css`
    overflow: hidden;
    height: 100%;
    padding: 0 !important;
    position: relative;
    -webkit-overflow-scrolling: touch;
  `,
  iframe: css`
    width: 100%;
    height: 100%;
    border: none;
    position: absolute;
    top: 0;
    left: 0;
  `,
  iframeContainer: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.4s ease-in-out;
    &.visible {
      opacity: 1;
    }
  `,
  modalBackDrop: css`
    z-index: 1000;
    /* iOS webview stacking context fixes */
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    will-change: transform;
  `,
  badge: css`
    min-width: 60px;
  `,
};

interface IframeModalProps {
  isVisible: boolean;
  onClose: () => void;
  isIframeUrlPending: boolean;
  url?: string;
  title?: React.ReactNode;
  pageContext?: ImmoTrackingContext;
}

export const ImmoAssistantModal = memo(
  ({ isVisible, onClose, isIframeUrlPending, url, title = 'Preview', pageContext = 'default' }: IframeModalProps) => {
    const isMeinplus = useHasMeinPlus();
    const queryClient = useQueryClient();
    const isMFE = window?.location?.pathname?.includes('/meine-immobilien') ?? false;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const trackingPageTitle = determineImmoPageTitle(pageContext);

    useModalFocus(isVisible);

    const getElements = useCallback(() => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return null;

      return {
        wrapper,
        skeleton: wrapper.querySelector('.skeleton-container'),
        iframeContainer: wrapper.querySelector('.iframe-wrapper'),
      };
    }, []);

    const showIframe = useCallback(() => {
      const elements = getElements();
      if (!elements) return;

      elements.skeleton?.classList.add('hidden');
      elements.iframeContainer?.classList.add('visible');
    }, [getElements]);

    const hideIframe = useCallback(() => {
      const elements = getElements();
      if (!elements) return;

      elements.skeleton?.classList.remove('hidden');
      elements.iframeContainer?.classList.remove('visible');
    }, [getElements]);

    const handleIframeLoad = () => {
      showIframe();
      report(
        {
          page: trackingPageTitle,
        },
        isMeinplus,
        null,
        isMFE ? PROPERTY_HUB_PAGE_TITLE_PREFIX : undefined,
      );
    };
    const handleClose = () => {
      report(
        {
          page: trackingPageTitle,
          ...(isMFE
            ? {
                ...TrackingEvents.AINAVIO_CHATBOT.CLOSE_BUTTON_CLICK_MFE,
              }
            : {
                ...TrackingEvents.AINAVIO_CHATBOT.CLOSE_BUTTON_CLICK,
              }),
        },
        isMeinplus,
        null,
        isMFE ? PROPERTY_HUB_PAGE_TITLE_PREFIX : undefined,
      );

      onClose();
    };

    const handleLicenseMessage = useCallback(
      async (event: MessageEvent) => {
        if (event.data.Tag === 'InvalidLicense') {
          hideIframe();
          queryClient.refetchQueries(['ainavio-assistant-iframe-url']);
        }
      },
      [hideIframe, queryClient],
    );

    useEffect(() => {
      if (isVisible) {
        window.addEventListener('message', handleLicenseMessage);
        document.body.style.overflow = 'hidden';
      }

      return () => {
        window.removeEventListener('message', handleLicenseMessage);
        document.body.style.overflow = 'auto';
      };
    }, [handleLicenseMessage, isVisible]);

    return (
      <ModalBackdrop visible={isVisible} onClose={handleClose} className={stylesModal.modalBackDrop}>
        <ModalContainer
          containerClassName={`Modal ${stylesModal.container}`}
          contentWrapperClassName={`absolute-reference ${stylesModal.modalContentWrapper}`}
          visible={isVisible}
          modalTitle={
            <span className='display-flex grid-align-center gap'>
              {title} <BetaBadgeSvg className={stylesModal.badge} />
            </span>
          }
          onClose={handleClose}
        >
          <ModalContent className={stylesModal.modalContent}>
            <div ref={wrapperRef}>
              <ImmoAssistantLoading />
              <div className={`iframe-wrapper ${stylesModal.iframeContainer}`}>
                {!isIframeUrlPending && (
                  <iframe
                    src={url}
                    className={stylesModal.iframe}
                    title={title as string}
                    allow='fullscreen'
                    onLoad={handleIframeLoad}
                  />
                )}
              </div>
            </div>
          </ModalContent>
        </ModalContainer>
      </ModalBackdrop>
    );
  },
);

export default ImmoAssistantModal;
